function hexToRgba(hex, alpha) {
    const raw = String(hex ?? "").replace("#", "").trim()
    if (raw.length !== 6) {
        return `rgba(0, 0, 0, ${alpha})`
    }
    const r = parseInt(raw.slice(0, 2), 16)
    const g = parseInt(raw.slice(2, 4), 16)
    const b = parseInt(raw.slice(4, 6), 16)
    return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

/**
 * @typedef {(
 *   | "boxed_up"
 *   | "confirmed"
 *   | "invoiced"
 *   | "new"
 *   | "partially_boxed_up"
 *   | "received"
 *   | "shipment_made"
 *   | "shipped"
 *   | "weighed_in"
 * )} OrderStatusKey
 */

/** @type {Record<OrderStatusKey, { backgroundColor: string, color: string, borderColor: string }>} */
export const ORDER_STATUS_BADGE_STYLE_BY_STATUS = {
    // v1 palette; backgrounds use higher alpha so tints read clearly vs each other.
    new: {
        color: "#9e2424",
        borderColor: "#9e2424",
        backgroundColor: hexToRgba("#d93f3f", 0.22),
    },
    // True green — distinct from boxed_up / weighed_in
    confirmed: {
        color: "#1a6b32",
        borderColor: "#1a6b32",
        backgroundColor: hexToRgba("#34b356", 0.28),
    },
    // Steel blue — distinct from shipment_made (indigo)
    shipped: {
        color: "#1a5f78",
        borderColor: "#1a5f78",
        backgroundColor: hexToRgba("#3491b3", 0.28),
    },
    received: {
        color: "#5a1f75",
        borderColor: "#5a1f75",
        backgroundColor: hexToRgba("#8d34b3", 0.24),
    },
    // Teal (not mint): reads clearly vs confirmed green + boxed lime
    weighed_in: {
        color: "#1f6b5a",
        borderColor: "#1f6b5a",
        backgroundColor: hexToRgba("#3aa892", 0.26),
    },
    partially_boxed_up: {
        color: "#b32e12",
        borderColor: "#b32e12",
        backgroundColor: hexToRgba("#ef4524", 0.22),
    },
    // Lime / yellow-green — pushed away from confirmed’s hue
    boxed_up: {
        color: "#4a7a22",
        borderColor: "#4a7a22",
        backgroundColor: hexToRgba("#80b740", 0.28),
    },
    // Indigo — darker than shipped’s blue
    shipment_made: {
        color: "#2a2e68",
        borderColor: "#2a2e68",
        backgroundColor: hexToRgba("#474ca0", 0.26),
    },
    invoiced: {
        color: "#141615",
        borderColor: "#141615",
        backgroundColor: hexToRgba("#343635", 0.18),
    },
}

/**
 * Accepts either raw API status values or formatted display labels.
 * @param {string | null | undefined} status
 * @returns {OrderStatusKey | null}
 */
export function normalizeOrderStatusKey(status) {
    const s = String(status ?? "").trim().toLowerCase()
    if (!s) {
        return null
    }

    // Raw API values
    if (s === "new") return "new"
    if (s === "confirmed") return "confirmed"
    if (s === "shipped") return "shipped"
    if (s === "received") return "received"
    if (s === "weighed_in") return "weighed_in"
    if (s === "partially_boxed_up") return "partially_boxed_up"
    if (s === "boxed_up") return "boxed_up"
    if (s === "shipment_made") return "shipment_made"
    if (s === "invoiced") return "invoiced"

    // Formatted labels used in v2 UI (`formatStatus`)
    if (s === "unconfirmed") return "new"
    if (s === "transferring") return "shipped"
    if (s === "weighed in") return "weighed_in"
    if (s === "partially boxed") return "partially_boxed_up"
    if (s === "all boxed up") return "boxed_up"
    if (s === "shipment made") return "shipment_made"

    return null
}

/**
 * @param {string | null | undefined} status
 * @returns {{ backgroundColor?: string, color?: string, borderColor?: string }}
 */
export function getOrderStatusBadgeStyle(status) {
    const key = normalizeOrderStatusKey(status)
    if (!key) {
        return {}
    }
    return ORDER_STATUS_BADGE_STYLE_BY_STATUS[key] ?? {}
}

